import React from 'react';
import { Box, Button, ButtonProps, CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

type DosenButtonSize = 'lg' | 'md' | 'sm';
type IconPosition = 'start' | 'end';

interface DosenButtonProps extends Omit<ButtonProps, 'color' | 'variant'> {
  sizeVariant?: DosenButtonSize;
  iconPosition?: IconPosition;
  icon?: React.ReactNode;
  tone?: 'cta' | 'basic';
  iconOnly?: boolean;
  locked?: boolean;
  loading?: boolean;
}

const gradientBg = 'linear-gradient(180deg, var(--orange-500, #F79420) 0%, var(--orange-700, #F15A26) 100%)';
const basicBg = 'var(--Fill-Button-button-basic-default, #1C1C1C)';
const textDefault = 'var(--Stroke-Button-button-cta-default, #FDFDFD)';

const paddingMap: Record<DosenButtonSize, { start: string; end: string; none: string }> = {
  lg: {
    start: '14px 16px 14px 14px',
    end: '14px 14px 14px 16px',
    none: '14px 16px'
  },
  md: {
    start: '10px 14px 10px 12px',
    end: '10px 12px 10px 14px',
    none: '10px 14px'
  },
  sm: {
    start: '4px 10px 4px 8px',
    end: '4px 8px 4px 10px',
    none: '4px 10px'
  }
};

const fontMap: Record<DosenButtonSize, { fontSize: number; lineHeight: number; borderRadius: number }> = {
  lg: { fontSize: 14, lineHeight: 23.8, borderRadius: 6 },
  md: { fontSize: 13, lineHeight: 19.5, borderRadius: 6 },
  sm: { fontSize: 12, lineHeight: 20.4, borderRadius: 4 }
};

const iconBoxMap: Record<DosenButtonSize, { box: number; padding: number }> = {
  lg: { box: 18, padding: 5 },
  md: { box: 18, padding: 5 },
  sm: { box: 16, padding: 5 }
};

const DosenButton: React.FC<DosenButtonProps> = ({
  children,
  sizeVariant = 'lg',
  iconPosition = 'start',
  icon,
  tone = 'cta',
  iconOnly = false,
  locked = false,
  loading = false,
  sx,
  disabled,
  ...rest
}) => {
  const hasIcon = Boolean(icon);
  const paddingVariant = hasIcon ? paddingMap[sizeVariant][iconPosition] : paddingMap[sizeVariant].none;
  const font = fontMap[sizeVariant];
  const iconBox = iconBoxMap[sizeVariant];
  const background = tone === 'cta' ? gradientBg : basicBg;
  const fontWeight = tone === 'cta' ? 600 : 500;
  const color = textDefault;
  const iconOnlyPadding = '5px';
  const iconOnlyRadius = 17;

  const isDisabled = loading || disabled;

  return (
    <Button
      {...rest}
      disabled={isDisabled}
      startIcon={!loading && !iconOnly && hasIcon && iconPosition === 'start' ? icon : undefined}
      endIcon={!loading && !iconOnly && hasIcon && iconPosition === 'end' ? icon : undefined}
      sx={{
        textTransform: 'none',
        background,
        color,
        fontFamily: 'Inter',
        fontWeight,
        fontSize: iconOnly ? undefined : font.fontSize,
        lineHeight: iconOnly ? undefined : `${font.lineHeight}px`,
        borderRadius: iconOnly ? `${iconOnlyRadius}px` : `${font.borderRadius}px`,
        padding: iconOnly ? iconOnlyPadding : paddingVariant,
        gap: iconOnly ? '0px' : '5px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        minHeight: 'unset',
        minWidth: iconOnly ? 'auto' : undefined,
        '&:hover': {
          background,
          opacity: 0.9
        },
        '&:disabled': {
          background,
          opacity: 0.5,
          color
        },
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
          margin: 0,
          width: iconBox.box,
          height: iconBox.box,
          padding: iconBox.padding,
          overflow: 'hidden'
        },
        '& .MuiSvgIcon-root': {
          color
        },
        ...sx
      }}
    >
      {loading ? <CircularProgress color="inherit" size={16} /> : children}
      {locked && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            color: 'white'
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.85)' }} />
        </Box>
      )}
    </Button>
  );
};

export default DosenButton;
